import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  api,
  fmtMoney,
  fmtPct,
  mondayOf,
  todayIso,
} from "@/lib/api";
import { ChevronUp, Plus, Target, Briefcase, Camera } from "lucide-react";

const DAY_NAMES = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const pad = (n) => String(n).padStart(2, "0");

/**
 * TaskBar — persistent ops strip across the bottom of the app.
 * Collapsed: single-line status. Expanded: detailed today-state.
 * Quick-toggle today's habits inline.
 */
export default function TaskBar() {
  const [open, setOpen] = useState(false);
  const [habits, setHabits] = useState([]);
  const [todayEntries, setTodayEntries] = useState({}); // habit_id -> bool
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [media, setMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(new Date());

  const today = todayIso();
  const todayJs = new Date();
  const dayIdx = (todayJs.getDay() + 6) % 7; // 0=Mon
  const weekStart = mondayOf(todayJs);

  const load = useCallback(async () => {
    const month = today.slice(0, 7);
    const arr = (v) => (Array.isArray(v) ? v : []);
    const [hs, es, ts, ds, ms, evs] = await Promise.all([
      api.get("/habits").then((r) => r.data).catch(() => []),
      api.get("/habits/entries", { params: { month } }).then((r) => r.data).catch(() => []),
      api.get("/weekly-tasks", { params: { week_start: weekStart } }).then((r) => r.data).catch(() => []),
      api.get("/deals").then((r) => r.data).catch(() => []),
      api.get("/media").then((r) => r.data).catch(() => []),
      api.get("/calendar-events").then((r) => r.data).catch(() => []),
    ]);
    setHabits(arr(hs));
    const m = {};
    arr(es).filter((e) => e.date === today).forEach((e) => (m[e.habit_id] = e.completed));
    setTodayEntries(m);
    setTasks(arr(ts));
    setDeals(arr(ds));
    setMedia(arr(ms));
    setEvents(arr(evs));
  }, [today, weekStart]);

  useEffect(() => {
    load();
    const t = setInterval(() => setNow(new Date()), 1000);
    const r = setInterval(load, 25000);
    return () => {
      clearInterval(t);
      clearInterval(r);
    };
  }, [load]);

  const toggleHabit = async (habitId) => {
    const next = !todayEntries[habitId];
    setTodayEntries((p) => ({ ...p, [habitId]: next }));
    try {
      await api.post("/habits/entries", {
        habit_id: habitId,
        date: today,
        completed: next,
      });
    } catch {
      setTodayEntries((p) => ({ ...p, [habitId]: !next }));
    }
  };

  // Today's tasks
  const todayTasks = tasks.filter((t) => t.day === dayIdx);
  const todayDone = todayTasks.filter((t) => t.done).length;
  const todayTotal = todayTasks.filter((t) => (t.title || "").trim()).length;
  const todayPct = todayTotal ? todayDone / todayTotal : 0;

  // Habit count
  const habitsDone = habits.filter((h) => todayEntries[h.id]).length;
  const habitsTotal = habits.length;
  const habitsPct = habitsTotal ? habitsDone / habitsTotal : 0;

  // Due today
  const followupsToday = deals.filter(
    (d) => d.next_follow_up === today && !d.closed
  );
  const shootsToday = media.filter(
    (m) => m.shoot_date === today && !m.media_done
  );
  const eventsToday = events.filter((e) => e.date === today);
  const dueCount = followupsToday.length + shootsToday.length + eventsToday.length;

  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;

  const dueChips = useMemo(() => {
    const arr = [];
    if (followupsToday.length)
      arr.push({
        kind: "deal",
        text: `${followupsToday.length} FOLLOW-UP${followupsToday.length > 1 ? "S" : ""}`,
        icon: Briefcase,
      });
    if (shootsToday.length)
      arr.push({
        kind: "media",
        text: `${shootsToday.length} SHOOT${shootsToday.length > 1 ? "S" : ""}`,
        icon: Camera,
      });
    if (eventsToday.length)
      arr.push({
        kind: "cal",
        text: `${eventsToday.length} EVENT${eventsToday.length > 1 ? "S" : ""}`,
        icon: Target,
      });
    return arr;
  }, [followupsToday.length, shootsToday.length, eventsToday.length]);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 print:hidden"
      data-testid="taskbar"
    >
      {/* expanded panel */}
      {open && (
        <div
          className="bg-background border-t border-border animate-fadein"
          data-testid="taskbar-expanded"
        >
          <div className="tread-plate h-1" />
          <div className="px-4 lg:px-8 py-5 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Habits */}
            <div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="kpi-label">Today · Habits</div>
                  <div className="display text-xl font-semibold mt-1">
                    {habitsDone}/{habitsTotal}{" "}
                    <span className="mono text-xs text-muted-foreground ml-1">
                      {fmtPct(habitsPct)}
                    </span>
                  </div>
                </div>
                <Link to="/month" className="hud-btn !px-2 !py-1" data-testid="tb-goto-month">
                  GRID
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-32 overflow-y-auto pr-1">
                {habits.map((h) => {
                  const on = !!todayEntries[h.id];
                  return (
                    <button
                      key={h.id}
                      onClick={() => toggleHabit(h.id)}
                      className="flex items-center gap-2 text-left group"
                      data-testid={`tb-habit-${h.id}`}
                    >
                      <span
                        className={`w-2.5 h-2.5 rounded-full border transition-all ${
                          on
                            ? "bg-[hsl(var(--oxblood))] border-[hsl(var(--oxblood))] shadow-[0_0_6px_hsl(var(--oxblood)/0.6)]"
                            : "border-border group-hover:border-[hsl(var(--oxblood))]"
                        }`}
                      />
                      <span
                        className={`text-xs truncate ${
                          on
                            ? "text-foreground"
                            : "text-muted-foreground group-hover:text-foreground"
                        }`}
                      >
                        {h.name}
                      </span>
                    </button>
                  );
                })}
                {habits.length === 0 && (
                  <span className="text-xs text-muted-foreground col-span-2">No habits yet.</span>
                )}
              </div>
            </div>

            {/* Today's weekly tasks */}
            <div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="kpi-label">Today · {DAY_NAMES[dayIdx]} Tasks</div>
                  <div className="display text-xl font-semibold mt-1">
                    {todayDone}/{todayTotal}{" "}
                    <span className="mono text-xs text-muted-foreground ml-1">
                      {fmtPct(todayPct)}
                    </span>
                  </div>
                </div>
                <Link to="/weekly" className="hud-btn !px-2 !py-1" data-testid="tb-goto-week">
                  OPEN
                </Link>
              </div>
              <div className="max-h-32 overflow-y-auto pr-1 space-y-1">
                {todayTasks.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    No tasks for today.
                  </span>
                )}
                {todayTasks.slice(0, 6).map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-2 text-xs"
                    data-testid={`tb-task-${t.id}`}
                  >
                    <span
                      className={`w-1.5 h-1.5 ${
                        t.done
                          ? "bg-[hsl(var(--oxblood))]"
                          : "bg-muted"
                      }`}
                    />
                    <span
                      className={`flex-1 truncate ${
                        t.done ? "line-through opacity-50" : ""
                      }`}
                    >
                      {t.title || <span className="opacity-50 italic">untitled</span>}
                    </span>
                  </div>
                ))}
                {todayTasks.length > 6 && (
                  <div className="mono text-[10px] text-muted-foreground tracking-widest">
                    + {todayTasks.length - 6} more
                  </div>
                )}
              </div>
            </div>

            {/* Due today */}
            <div>
              <div className="flex items-end justify-between mb-3">
                <div>
                  <div className="kpi-label">Due Today</div>
                  <div className="display text-xl font-semibold mt-1 flex items-center gap-2">
                    {dueCount}
                    {dueCount > 0 && <span className="status-dot" />}
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 max-h-32 overflow-y-auto pr-1">
                {followupsToday.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 text-xs">
                    <Briefcase size={11} className="text-hud" />
                    <span className="flex-1 truncate">
                      {d.client || "Untitled"}{" "}
                      <span className="text-muted-foreground">
                        / follow-up
                      </span>
                    </span>
                    <span className="hud-chip !px-1.5 !py-0">{d.priority || "Med"}</span>
                  </div>
                ))}
                {shootsToday.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 text-xs">
                    <Camera size={11} className="text-hud" />
                    <span className="flex-1 truncate">
                      {m.client || "Shoot"}{" "}
                      <span className="text-muted-foreground">
                        / {m.shoot_time || "TBD"}
                      </span>
                    </span>
                  </div>
                ))}
                {eventsToday.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 text-xs">
                    <Target size={11} className="text-hud" />
                    <span className="flex-1 truncate">
                      {e.title || "Event"}{" "}
                      <span className="text-muted-foreground">
                        / {e.start_time || "—"}
                      </span>
                    </span>
                  </div>
                ))}
                {dueCount === 0 && (
                  <span className="text-xs text-muted-foreground">
                    Nothing on the schedule today.
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Link to="/deals" className="hud-btn !py-1.5 flex items-center justify-center gap-2">
                  <Plus size={11} /> DEAL
                </Link>
                <Link to="/media" className="hud-btn !py-1.5 flex items-center justify-center gap-2">
                  <Plus size={11} /> ORDER
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* compact bar */}
      <div
        className="bg-background/95 backdrop-blur-md border-t border-border"
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <div className="tread-plate h-1" />
        <div className="flex items-center gap-3 lg:gap-5 px-3 lg:px-8 h-10 cursor-pointer select-none overflow-x-auto no-scrollbar">
          <span className="mono uppercase text-[10px] tracking-[0.22em] text-muted-foreground whitespace-nowrap">
            <span className="text-foreground font-semibold">TODAY</span>{" "}
            <span className="hidden sm:inline">· {DAY_NAMES[dayIdx]} {todayJs.toLocaleDateString("en-US", { month: "short", day: "numeric" }).toUpperCase()}</span>
          </span>

          <span className="hidden md:flex items-center gap-2 mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground whitespace-nowrap">
            <Target size={11} className="text-hud" />
            HABITS
            <span className="text-foreground font-semibold tabular-nums">
              {habitsDone}/{habitsTotal}
            </span>
            <div className="ox-progress !h-1 w-20"><span style={{ width: `${habitsPct * 100}%` }} /></div>
          </span>

          {/* habit dot row - compact toggles */}
          <div className="hidden lg:flex items-center gap-1.5">
            {habits.map((h) => {
              const on = !!todayEntries[h.id];
              return (
                <button
                  key={h.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleHabit(h.id);
                  }}
                  title={h.name}
                  data-testid={`tb-dot-${h.id}`}
                  className={`w-2 h-2 rounded-full border transition-all ${
                    on
                      ? "bg-[hsl(var(--oxblood))] border-[hsl(var(--oxblood))] shadow-[0_0_4px_hsl(var(--oxblood)/0.7)]"
                      : "border-border hover:border-[hsl(var(--oxblood))]"
                  }`}
                />
              );
            })}
          </div>

          <span className="hidden sm:flex items-center gap-2 mono uppercase text-[10px] tracking-[0.2em] text-muted-foreground whitespace-nowrap">
            <ChevronUp size={11} />
            TASKS
            <span className="text-foreground font-semibold tabular-nums">
              {todayDone}/{todayTotal}
            </span>
            <div className="ox-progress !h-1 w-20"><span style={{ width: `${todayPct * 100}%` }} /></div>
          </span>

          {dueChips.map((c, i) => {
            const Icon = c.icon;
            return (
              <span
                key={i}
                className="hud-chip hud-chip-on whitespace-nowrap"
              >
                <Icon size={10} /> {c.text}
              </span>
            );
          })}

          <span className="flex-1" />

          <span className="mono uppercase text-[10px] tracking-[0.22em] text-muted-foreground whitespace-nowrap">
            <span className="text-foreground font-semibold tabular-nums">{time}</span>
          </span>

          <ChevronUp
            size={13}
            className={`text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>
    </div>
  );
}
